// ═══════════════════════════════════════════════════════════════════
//  SheetTabStrip — horizontal pills above the minimap canvas.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase D of MINIMAP_ATLAS_PLAN. Hidden when only one sheet exists.
//  Each pill shows the sheet name + block count; the active sheet is
//  filled, others outlined. Click → onSwitchSheet(sheetId).
//
// ═══════════════════════════════════════════════════════════════════

import { cn } from '@/lib/utils';
import type { MinimapSheetTab } from '@/types/minimap';

export interface SheetTabStripProps {
  tabs: MinimapSheetTab[];
  onSwitchSheet?: (sheetId: string) => void;
  className?: string;
}

export function SheetTabStrip({ tabs, onSwitchSheet, className }: SheetTabStripProps) {
  if (tabs.length <= 1) return null;
  return (
    <div
      className={cn(
        'flex h-[18px] items-center gap-1 overflow-x-auto px-1 py-0.5 text-[10px]',
        '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      role="tablist"
      aria-label="Sheet tabs"
      data-fw-minimap-tabstrip="true"
    >
      {tabs.map((t) => (
        <button
          key={t.sheetId}
          type="button"
          role="tab"
          aria-selected={t.isActive}
          onClick={() => onSwitchSheet?.(t.sheetId)}
          className={cn(
            'shrink-0 rounded px-1.5 py-0.5 transition-colors',
            t.isActive
              ? 'bg-primary text-primary-foreground'
              : 'border border-border bg-background hover:bg-accent',
          )}
          title={`${t.title} · ${t.blockCount} blocks`}
          data-fw-minimap-sheet={t.sheetId}
        >
          <span className="max-w-[6rem] truncate">{t.title}</span>
          <span className="ml-1 opacity-60">{t.blockCount}</span>
        </button>
      ))}
    </div>
  );
}
